import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getJob, getJobVersion } from "../services/apiService";
import { alignLineItemResults } from "../utils/jobPayload";
import {
  getFinalUnitPrice,
  getItemQuantity,
  getLineAmount,
  getLineDisplayName,
} from "./workbench/estimate/qboLine";
import { formatJobDate } from "./workbench/JobsRail";
import { formatCurrency } from "../utils/formatters";
import "./QuoteView.css";

/**
 * `/jobs/:jobId/quote` — the quote as a document.
 *
 * Until this page there was no way for a finished quote to leave the app: no
 * print, no PDF, no export. So it is deliberately a sheet of paper rather than
 * another workbench — one page, the lines, one Total, and a print stylesheet
 * that drops every pixel of app chrome so the browser's own "Save as PDF" is
 * the whole export story.
 *
 * MONEY RULE: this page reads stored numbers and nothing else. It never calls
 * /api/calculate and never derives a figure. A version saved before the engine
 * stored its results has no price to show, and says so — quietly recomputing
 * one would put a number in front of a customer that no saved record backs.
 */

/** The version's own quoted total, read the way the version history reads it. */
function readQuotedTotal(version) {
  const stored = version?.calculation_results?.final_suggested_sale_price_P105;
  if (stored !== null && stored !== undefined && stored !== "") return stored;
  return version?.final_quoted_price ?? null;
}

/**
 * The priced lines, paired back to the rows the estimator typed.
 *
 * Alignment first: a job carrying an unaccepted AI draft is priced one line
 * shorter than it is saved, so a naive index pairing slides every later line's
 * money onto its neighbour's name. Lines that did not price are left out — a
 * quote is not the place to show the customer a line the engine refused.
 */
function buildQuoteLines(version) {
  const lineItems = Array.isArray(version?.job_data?.line_items)
    ? version.job_data.line_items
    : [];
  const results = alignLineItemResults(version?.calculation_results, lineItems);
  const priced = Array.isArray(results?.line_item_results)
    ? results.line_item_results
    : [];

  return priced
    .map((item, index) => ({ item, lineItem: lineItems[index] || null, index }))
    .filter(({ item }) => item && !item.error)
    .map(({ item, lineItem, index }) => ({
      key: `line-${index}`,
      name: getLineDisplayName(item, lineItem) || `Line ${index + 1}`,
      description:
        typeof item?.input_data?.description === "string"
          ? item.input_data.description.trim()
          : "",
      quantity: getItemQuantity(item),
      unitPrice: getFinalUnitPrice(item),
      amount: getLineAmount(item),
    }));
}

function countErroredLines(version) {
  const lineItems = Array.isArray(version?.job_data?.line_items)
    ? version.job_data.line_items
    : [];
  const results = alignLineItemResults(version?.calculation_results, lineItems);
  return Array.isArray(results?.line_item_results)
    ? results.line_item_results.filter((item) => item && item.error).length
    : 0;
}

function QuoteView() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [version, setVersion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const load = async () => {
      try {
        const jobData = await getJob(jobId);
        const versionData = await getJobVersion(
          jobId,
          jobData.current_version_id,
        );
        if (!isMounted) return;
        setJob(jobData);
        setVersion(versionData);
      } catch (loadError) {
        console.error("Failed to load quote:", loadError);
        if (isMounted) setError("Couldn't load this quote.");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [jobId, reloadToken]);

  const retry = useCallback(() => setReloadToken((token) => token + 1), []);

  if (isLoading) {
    return (
      <div className="quote-view">
        <p className="quote-view__state">Loading quote…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quote-view">
        <p className="quote-view__state quote-view__state--error" role="alert">
          {error}
        </p>
        <div className="quote-view__bar">
          <button type="button" className="wb-tbtn" onClick={retry}>
            Try again
          </button>
          <button
            type="button"
            className="wb-tbtn"
            onClick={() => navigate("/jobs")}
          >
            Back to saved jobs
          </button>
        </div>
      </div>
    );
  }

  const total = readQuotedTotal(version);
  const hasPricing = Boolean(version?.calculation_results);
  const lines = hasPricing ? buildQuoteLines(version) : [];
  const erroredLineCount = hasPricing ? countErroredLines(version) : 0;

  return (
    <div className="quote-view">
      <div className="quote-view__bar quote-view__noprint">
        <button
          type="button"
          className="wb-tbtn"
          onClick={() => navigate("/jobs")}
        >
          Back to saved jobs
        </button>
        <button
          type="button"
          className="wb-tbtn wb-tbtn--solid"
          onClick={() => window.print()}
        >
          Print
        </button>
      </div>

      <article className="quote-doc">
        <header className="quote-doc__hd">
          <h1 className="quote-doc__title">
            {job?.job_name || "Untitled job"}
          </h1>
          <p className="quote-doc__customer">
            {job?.customer_name || "No customer on file"}
          </p>
          <dl className="quote-doc__meta">
            <div>
              <dt>Date</dt>
              <dd>{formatJobDate(version?.created_at || job?.updated_at)}</dd>
            </div>
            <div>
              <dt>Version</dt>
              <dd>
                {version?.version_number === null ||
                version?.version_number === undefined
                  ? "—"
                  : String(version.version_number)}
              </dd>
            </div>
          </dl>
        </header>

        {erroredLineCount > 0 ? (
          <p className="quote-doc__incomplete" role="alert">
            Incomplete quote: {erroredLineCount}{" "}
            {erroredLineCount === 1 ? "line is" : "lines are"} not included in
            pricing.
          </p>
        ) : null}

        {!hasPricing ? (
          <p className="quote-doc__empty">This version has no stored pricing</p>
        ) : lines.length === 0 ? (
          <p className="quote-doc__empty">This version has no priced lines</p>
        ) : (
          <table className="quote-doc__table">
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col" className="quote-doc__num">
                  Qty
                </th>
                <th scope="col" className="quote-doc__num">
                  Unit price
                </th>
                <th scope="col" className="quote-doc__num">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.key}>
                  <th scope="row">
                    <span className="quote-doc__name">{line.name}</span>
                    {line.description ? (
                      <span className="quote-doc__desc">
                        {line.description}
                      </span>
                    ) : null}
                  </th>
                  <td className="quote-doc__num">{line.quantity}</td>
                  <td className="quote-doc__num">
                    {formatCurrency(line.unitPrice)}
                  </td>
                  <td className="quote-doc__num">
                    {formatCurrency(line.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {hasPricing &&
        erroredLineCount === 0 &&
        total !== null &&
        total !== undefined ? (
          <>
            <div className="quote-doc__total">
              <span>Total</span>
              <b>{formatCurrency(total)}</b>
            </div>
            {Number(version?.calculation_results?.sales_tax_P99) > 0 ? (
              <p className="quote-doc__tax">
                Includes{" "}
                {formatCurrency(version.calculation_results.sales_tax_P99)}{" "}
                sales tax
              </p>
            ) : null}
          </>
        ) : null}
      </article>
    </div>
  );
}

export default QuoteView;
